import {
  TAX_PRESETS,
  type Formula,
  type MonthIndex,
  type Scheduled,
  type TaxBracket,
  type TaxDeduction,
  type TaxPresetId,
  type TaxSpec,
} from '@finsim/model';
import { useUnit } from 'effector-react';
import { CalendarRange, ExternalLink, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScheduledNumberField, ScheduleEditor, topRate } from '../../../entities/project';
import { $result } from '../../../entities/simulation';
import { $valueMode } from '../../../entities/view-settings';
import { cn } from '../../../shared/lib/cn';
import { formatDate, formatMoney, formatNumber, formatPercent } from '../../../shared/lib/format';
import { useLocale } from '../../../shared/lib/i18n/use-locale';
import {
  Button,
  EditOnly,
  Field,
  IconButton,
  MoneyField,
  MonthField,
  OptionalMoneyField,
  PercentField,
  Section,
  Select,
  Switch,
  TextField,
  Tooltip,
} from '../../../shared/ui';
import { applyTaxPreset, newDeductionId, nextBracket, sortBrackets, withOptional } from '../lib';
import type { FormContext } from './context';

type OnChange = (spec: TaxSpec, field: string) => void;

const PRESET_IDS = Object.keys(TAX_PRESETS) as TaxPresetId[];

function isPresetId(id: string | undefined): id is TaxPresetId {
  return id !== undefined && id in TAX_PRESETS;
}

/** Rules, deductions and brackets of a `tax` formula (docs/PLAN.md §3.2). */
export function TaxSections({
  spec,
  ctx,
  onChange,
}: {
  spec: TaxSpec;
  ctx: FormContext;
  onChange: OnChange;
}) {
  return (
    <>
      <RulesSection spec={spec} ctx={ctx} onChange={onChange} />
      <DeductionsSection spec={spec} ctx={ctx} onChange={onChange} />
      <BracketsSection spec={spec} ctx={ctx} onChange={onChange} />
    </>
  );
}

function RulesSection({
  spec,
  ctx,
  onChange,
}: {
  spec: TaxSpec;
  ctx: FormContext;
  onChange: OnChange;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const preset = isPresetId(spec.presetId) ? TAX_PRESETS[spec.presetId] : null;
  const { start, horizonMonths } = ctx.project.assumptions;
  return (
    <Section title={t('inspector.sections.tax')}>
      <Field
        // Stacked: preset names are long.
        className="grid-cols-1 [&>label]:pt-0"
        label={t('tax.preset')}
        hint={
          preset
            ? t('tax.presetHint', { date: formatDate(preset.asOf, locale) })
            : t('tax.customHint')
        }
      >
        {({ id }) => (
          <Select<TaxPresetId | 'custom'>
            id={id}
            value={isPresetId(spec.presetId) ? spec.presetId : 'custom'}
            options={[
              ...PRESET_IDS.map((p) => ({ value: p, label: t(`tax.presets.${p}`) })),
              { value: 'custom' as const, label: t('tax.custom') },
            ]}
            onValueChange={(value) => {
              onChange(
                applyTaxPreset(spec, value === 'custom' ? null : value, {
                  start,
                  horizonMonths,
                  contributionsName: t('tax.contributions'),
                }),
                'preset',
              );
            }}
          />
        )}
      </Field>
      {preset && (
        <details className="group rounded-md bg-bg-2 px-2.5 py-2 text-xs">
          <summary className="cursor-pointer text-text-2 outline-none focus-visible:ring-2 focus-visible:ring-focus">
            {t('tax.sources')}
          </summary>
          <ul className="mt-1.5 space-y-1">
            {preset.sources.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-start gap-1 text-text-2 underline decoration-border-strong underline-offset-2 hover:text-text-1"
                >
                  <span>{s.title}</span>
                  <ExternalLink className="mt-0.5 size-3 shrink-0" aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
      <Field label={t('tax.settlement')} hint={t('tax.settlementHint')}>
        {({ id }) => (
          <Select
            id={id}
            value={spec.settlement}
            options={[
              { value: 'monthly', label: t('tax.settlements.monthly') },
              { value: 'annualCumulative', label: t('tax.settlements.annualCumulative') },
            ]}
            onValueChange={(settlement) => {
              onChange({ ...spec, settlement }, 'settlement');
            }}
          />
        )}
      </Field>
      <Field label={t('tax.indexThresholds')}>
        {({ id }) => (
          <Select
            id={id}
            value={spec.indexThresholds}
            options={[
              { value: 'none', label: t('tax.indexings.none') },
              { value: 'cpi', label: t('tax.indexings.cpi') },
            ]}
            onValueChange={(indexThresholds) => {
              onChange({ ...spec, indexThresholds }, 'indexThresholds');
            }}
          />
        )}
      </Field>
      <OptionalScheduledMoneyField
        label={t('tax.allowance')}
        value={spec.allowance}
        ctx={ctx}
        onCommit={(allowance) => {
          onChange(withOptional(spec, 'allowance', allowance), 'allowance');
        }}
      />
      <OptionalScheduledMoneyField
        label={t('tax.credits')}
        value={spec.credits}
        ctx={ctx}
        onCommit={(credits) => {
          onChange(withOptional(spec, 'credits', credits), 'credits');
        }}
      />
    </Section>
  );
}

function DeductionsSection({
  spec,
  ctx,
  onChange,
}: {
  spec: TaxSpec;
  ctx: FormContext;
  onChange: OnChange;
}) {
  const { t } = useTranslation();
  const setDeduction = (index: number, next: TaxDeduction, field: string): void => {
    onChange(
      { ...spec, deductions: spec.deductions.map((d, i) => (i === index ? next : d)) },
      `deduction:${next.id}:${field}`,
    );
  };
  return (
    <Section
      title={t('inspector.sections.deductions')}
      actions={
        <EditOnly>
          {spec.deductions.length < 10 && (
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<Plus />}
              onClick={() => {
                onChange(
                  {
                    ...spec,
                    deductions: [
                      ...spec.deductions,
                      {
                        id: newDeductionId(spec.deductions),
                        name: t('tax.newDeduction', { n: spec.deductions.length + 1 }),
                        rate: { kind: 'constant', value: 0.1 },
                        emitAsFlow: true,
                      },
                    ],
                  },
                  'deductions',
                );
              }}
            >
              {t('tax.addDeduction')}
            </Button>
          )}
        </EditOnly>
      }
    >
      {spec.deductions.length === 0 && (
        <p className="text-xs text-text-3">{t('tax.noDeductions')}</p>
      )}
      {spec.deductions.map((d, index) => (
        <fieldset
          key={d.id}
          aria-label={d.name}
          className="space-y-2 rounded-md border border-border-subtle p-2.5"
        >
          <div className="flex items-center gap-1.5">
            <TextField
              aria-label={t('tax.deductionName')}
              className="min-w-0 flex-1"
              value={d.name}
              maxLength={80}
              onCommit={(name) => {
                setDeduction(index, { ...d, name }, 'name');
              }}
            />
            <EditOnly>
              <IconButton
                label={t('tax.removeDeduction', { name: d.name })}
                size="md"
                onClick={() => {
                  onChange(
                    { ...spec, deductions: spec.deductions.filter((_, i) => i !== index) },
                    'deductions',
                  );
                }}
              >
                <X />
              </IconButton>
            </EditOnly>
          </div>
          <ScheduledNumberField
            label={t('tax.deductionRate')}
            value={d.rate}
            project={ctx.project}
            format={ctx.percent}
            onCommit={(rate) => {
              setDeduction(index, { ...d, rate }, 'rate');
            }}
            renderInput={(value, onCommit, label, id) => (
              <PercentField
                id={id}
                aria-label={label}
                value={value}
                min={0}
                max={100}
                onCommit={onCommit}
              />
            )}
          />
          <OptionalScheduledMoneyField
            label={t('tax.deductionFloor')}
            hint={t('tax.perMonth')}
            value={d.floor}
            ctx={ctx}
            onCommit={(floor) => {
              setDeduction(index, withOptional(d, 'floor', floor), 'floor');
            }}
          />
          <OptionalScheduledMoneyField
            label={t('tax.deductionCap')}
            hint={t('tax.perMonth')}
            value={d.cap}
            ctx={ctx}
            onCommit={(cap) => {
              setDeduction(index, withOptional(d, 'cap', cap), 'cap');
            }}
          />
          <Field label={t('tax.emit')} hint={t('tax.emitHint')}>
            {({ id, describedBy }) => (
              <div className="flex h-7 items-center">
                <Switch
                  id={id}
                  aria-describedby={describedBy}
                  checked={d.emitAsFlow}
                  onCheckedChange={(emitAsFlow) => {
                    setDeduction(index, { ...d, emitAsFlow }, 'emit');
                  }}
                />
              </div>
            )}
          </Field>
        </fieldset>
      ))}
    </Section>
  );
}

function BracketsSection({
  spec,
  ctx,
  onChange,
}: {
  spec: TaxSpec;
  ctx: FormContext;
  onChange: OnChange;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const { brackets } = spec;
  const commit = (next: Scheduled<TaxBracket[]>): void => {
    onChange({ ...spec, brackets: next }, 'brackets');
  };
  const label = t('inspector.sections.brackets');
  return (
    <Section
      title={label}
      actions={
        <EditOnly>
          {brackets.kind === 'constant' && (
            <Tooltip content={t('schedule.varyOverTime')}>
              <IconButton
                label={`${t('schedule.varyOverTime')} — ${label}`}
                onClick={() => {
                  setEditing(true);
                }}
              >
                <CalendarRange />
              </IconButton>
            </Tooltip>
          )}
        </EditOnly>
      }
    >
      <p className="text-xs text-text-3">{t('tax.bracketsHint')}</p>
      {brackets.kind === 'constant' ? (
        <BracketsEditor
          value={brackets.value}
          symbol={ctx.symbol}
          onChange={(value) => {
            commit({ kind: 'constant', value });
          }}
        />
      ) : (
        <Button
          variant="secondary"
          className="h-auto min-h-7 w-full justify-start py-1 text-left whitespace-normal"
          leadingIcon={<CalendarRange />}
          onClick={() => {
            setEditing(true);
          }}
        >
          <span className="text-xs">
            {t('schedule.summary', {
              count: brackets.segments.length,
              from: ctx.percent(topRate(brackets.segments[0]?.value ?? [])),
              to: ctx.percent(topRate(brackets.segments.at(-1)?.value ?? [])),
            })}
          </span>
        </Button>
      )}
      <ScheduleEditor<TaxBracket[]>
        open={editing}
        onOpenChange={setEditing}
        title={t('schedule.title', { label })}
        value={brackets}
        start={ctx.project.assumptions.start}
        horizon={ctx.project.assumptions.horizonMonths}
        onSave={commit}
        renderValue={(value, onValueChange) => (
          <BracketsEditor value={value} symbol={ctx.symbol} onChange={onValueChange} />
        )}
        toNumber={(value) => topRate(value)}
        formatPreview={ctx.percent}
      />
    </Section>
  );
}

/** Marginal brackets: annual threshold + rate per row; the first always starts at 0. */
function BracketsEditor({
  value,
  symbol,
  onChange,
}: {
  value: readonly TaxBracket[];
  symbol: string;
  onChange: (value: TaxBracket[]) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const update = (index: number, next: TaxBracket): void => {
    onChange(sortBrackets(value.map((b, i) => (i === index ? next : b))));
  };
  return (
    <div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-text-3">
            <th scope="col" className="pb-1 text-left font-normal">
              {t('tax.bracketFrom')}
            </th>
            <th scope="col" className="w-24 pb-1 pl-1.5 text-left font-normal">
              {t('tax.bracketRate')}
            </th>
            <th scope="col" className="w-8 pb-1">
              <span className="sr-only">{t('schedule.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {value.map((b, index) => {
            const n = index + 1;
            return (
              <tr key={index}>
                <td className="py-0.5">
                  {index === 0 ? (
                    <p className="tabular flex h-7 items-center px-2 text-text-2">
                      {formatNumber(0, locale)} {symbol}
                    </p>
                  ) : (
                    <MoneyField
                      aria-label={`${t('tax.bracketN', { n })} — ${t('tax.bracketFrom')}`}
                      value={b.from}
                      min={0.01}
                      symbol={symbol}
                      onCommit={(from) => {
                        update(index, { ...b, from });
                      }}
                    />
                  )}
                </td>
                <td className="py-0.5 pl-1.5">
                  <PercentField
                    aria-label={`${t('tax.bracketN', { n })} — ${t('tax.bracketRate')}`}
                    value={b.rate}
                    min={0}
                    max={100}
                    onCommit={(rate) => {
                      update(index, { ...b, rate });
                    }}
                  />
                </td>
                <td className="py-0.5 text-right">
                  {index > 0 && (
                    <EditOnly>
                      <IconButton
                        label={t('tax.removeBracket', { n })}
                        onClick={() => {
                          onChange(value.filter((_, i) => i !== index));
                        }}
                      >
                        <Trash2 />
                      </IconButton>
                    </EditOnly>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <EditOnly>
        {value.length < 20 && (
          <Button
            size="sm"
            variant="ghost"
            className="mt-1"
            leadingIcon={<Plus />}
            onClick={() => {
              onChange([...value, nextBracket(value)]);
            }}
          >
            {t('tax.addBracket')}
          </Button>
        )}
      </EditOnly>
    </div>
  );
}

/**
 * An optional amount that may vary over time (allowances, contribution floors and caps). Emptying
 * the main input removes it; periods inside the schedule editor always need a value.
 */
function OptionalScheduledMoneyField({
  label,
  hint,
  value,
  ctx,
  onCommit,
}: {
  label: string;
  hint?: string;
  value: Scheduled<number> | undefined;
  ctx: FormContext;
  onCommit: (value: Scheduled<number> | undefined) => void;
}) {
  const { t } = useTranslation();
  if (value === undefined) {
    return (
      <Field label={label} hint={hint}>
        {({ id, describedBy }) => (
          <OptionalMoneyField
            id={id}
            aria-describedby={describedBy}
            value={undefined}
            symbol={ctx.symbol}
            placeholder={t('fields.noLimit')}
            onCommit={(v) => {
              if (v !== undefined) onCommit({ kind: 'constant', value: v });
            }}
          />
        )}
      </Field>
    );
  }
  return (
    <ScheduledNumberField
      label={label}
      value={value}
      project={ctx.project}
      format={ctx.money}
      onCommit={onCommit}
      renderInput={(v, commit, inputLabel, id) =>
        // The main (constant) input has an id; schedule periods don't and must keep a value.
        id === undefined ? (
          <MoneyField aria-label={inputLabel} value={v} symbol={ctx.symbol} onCommit={commit} />
        ) : (
          <OptionalMoneyField
            id={id}
            aria-label={inputLabel}
            value={v}
            symbol={ctx.symbol}
            placeholder={t('fields.noLimit')}
            onCommit={(next) => {
              if (next === undefined) onCommit(undefined);
              else commit(next);
            }}
          />
        )
      }
    />
  );
}

/**
 * One month of the tax, from the simulation: taxed income → deductions → taxable → tax → net.
 * Defaults to the first month with taxed income; any month can be picked.
 */
export function TaxBreakdown({
  item,
  spec,
  ctx,
}: {
  item: Formula;
  spec: TaxSpec;
  ctx: FormContext;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [result, valueMode] = useUnit([$result, $valueMode]);
  const [picked, setPicked] = useState<MonthIndex | null>(null);
  const series = result?.scenarios[ctx.scenarioId]?.items[item.id];
  const breakdown = series?.type === 'flow' ? series.tax : undefined;
  if (!result || !breakdown) return null;

  const base = breakdown.base[valueMode];
  const firstTaxed = base.findIndex((v, i) => i >= item.start && v > 0);
  const month = picked ?? ((firstTaxed < 0 ? item.start : firstTaxed) as MonthIndex);
  const at = (values: ArrayLike<number>): number => values[month] ?? 0;
  const money = (v: number): string =>
    formatMoney(v, locale, { currency: ctx.project.assumptions.currency, cents: true }).replace(
      '-',
      '−',
    );

  const gross = at(base);
  const deductions = spec.deductions.map((d) => ({
    d,
    amount: at(breakdown.deductions[d.id]?.[valueMode] ?? []),
  }));
  const tax = at(breakdown.tax[valueMode]);
  const paid = deductions.reduce((sum, x) => sum + (x.d.emitAsFlow ? x.amount : 0), 0);
  const net = gross - paid - tax;

  const rows: { key: string; label: string; value: string; note?: string; strong?: boolean }[] = [
    { key: 'gross', label: t('tax.gross'), value: money(gross), strong: true },
    ...deductions.map(({ d, amount }) => ({
      key: d.id,
      label: d.name,
      value: money(-amount),
      ...(d.emitAsFlow ? {} : { note: t('tax.notDeducted') }),
    })),
    { key: 'taxable', label: t('tax.taxable'), value: money(at(breakdown.taxable[valueMode])) },
    { key: 'tax', label: t('tax.incomeTax'), value: money(-tax) },
    { key: 'net', label: t('tax.net'), value: money(net), strong: true },
    {
      key: 'effective',
      label: t('tax.effectiveRate'),
      value: gross > 0 ? formatPercent((paid + tax) / gross, locale, 2) : '–',
    },
  ];

  return (
    <Section title={t('inspector.sections.breakdown')}>
      <Field label={t('tax.breakdownMonth')}>
        {({ id }) => (
          <MonthField
            id={id}
            value={month}
            start={result.startYearMonth}
            max={result.months - 1}
            onCommit={setPicked}
          />
        )}
      </Field>
      {gross > 0 ? (
        <dl className="rounded-md bg-bg-2 px-2.5 py-1.5 text-xs">
          {rows.map((r) => (
            <div
              key={r.key}
              className={cn(
                'flex items-baseline justify-between gap-3 py-1',
                (r.key === 'taxable' || r.key === 'net') && 'border-t border-border-subtle',
              )}
            >
              <dt className={cn('min-w-0 text-text-3', r.strong && 'text-text-2')}>
                {r.label}
                {r.note && <span className="block text-2xs">{r.note}</span>}
              </dt>
              <dd
                className={cn(
                  'tabular shrink-0 text-text-2',
                  r.strong && 'font-medium text-text-1',
                )}
              >
                {r.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-xs text-text-3">{t('tax.breakdownEmpty')}</p>
      )}
    </Section>
  );
}
